/**
 * O que faz: Mostra todos os anúncios em lista compacta, com busca, ordenação e edição de apelidos.
 * Onde usa: Modal aberto pelo card de Performance de Anúncios no dashboard.
 * Conexões: useAnunciosDashboard, useAdAliases, Dialog e drill-down de leads.
 * Tabelas: Nenhuma consulta direta; usa agregados fornecidos pelos hooks.
 * Data: 31/08/2026.
 */

import { useEffect, useMemo, useState } from 'react';
import { ArrowUpDown, Check, Edit2, Search, Target, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAdAliases } from '@/hooks/useAdAliases';
import { useAnunciosDashboard } from '@/hooks/dashboard/useAnunciosDashboard';
import type { ContextoDashboard } from '@/features/dashboard/data/dashboardContracts';

interface AdPerformanceData { anuncio: string; leads: number; conversoes: number; }
interface LinhaAnuncio { chave: string; nome: string; apelido?: string | null; leads: number; conversoes: number; }
interface AdPerformanceModalProps {
  isOpen: boolean; onClose: () => void; data: AdPerformanceData[]; onAdClick?: (adName: string) => void;
  adminMode?: boolean; targetClinicaId?: string; contexto?: ContextoDashboard;
}
type Ordem = 'leads' | 'conversoes' | 'taxa' | 'nome';
type Direcao = 'asc' | 'desc';
type Tamanho = 20 | 50;
const taxa = (leads: number, conversoes: number) => leads ? (conversoes / leads) * 100 : 0;

export const AdPerformanceModal = ({ isOpen, onClose, data, onAdClick, adminMode = false, targetClinicaId, contexto }: AdPerformanceModalProps) => {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>('leads');
  const [direcao, setDirecao] = useState<Direcao>('desc');
  const [tamanho, setTamanho] = useState<Tamanho>(20);
  const [pagina, setPagina] = useState(1);
  const [editando, setEditando] = useState<string | null>(null);
  const [apelido, setApelido] = useState('');
  const [erroApelido, setErroApelido] = useState<string | null>(null);
  const [apelidoSalvo, setApelidoSalvo] = useState(false);
  const { getAliasForAd, saveAlias, isSaving } = useAdAliases(adminMode ? targetClinicaId : undefined);
  // Com contexto, a busca, ordem e página vão ao servidor e não baixam toda a lista.
  const consulta = useAnunciosDashboard(contexto, { aberto: isOpen, busca, ordem, direcao, pagina, tamanho });
  const remoto = Boolean(contexto);

  const legados = useMemo(() => {
    // O caminho remoto nunca recalcula o array local: dados locais não podem mascarar uma falha da consulta.
    if (remoto) return [];
    const termo = busca.trim().toLocaleLowerCase('pt-BR');
    return data.map(item => ({ chave: item.anuncio, nome: item.anuncio, leads: item.leads, conversoes: item.conversoes }))
      .filter(item => !termo || item.nome.toLocaleLowerCase('pt-BR').includes(termo) || (getAliasForAd(item.nome) ?? '').toLocaleLowerCase('pt-BR').includes(termo))
      .sort((a, b) => {
        const valor = (item: LinhaAnuncio): number | string => ordem === 'leads' ? item.leads : ordem === 'conversoes' ? item.conversoes : ordem === 'taxa' ? taxa(item.leads, item.conversoes) : getAliasForAd(item.nome) ?? item.nome;
        const primeiro = valor(a); const segundo = valor(b);
        const resultado = typeof primeiro === 'string' && typeof segundo === 'string' ? primeiro.localeCompare(segundo, 'pt-BR') : Number(primeiro) - Number(segundo);
        return direcao === 'asc' ? resultado : -resultado;
      });
  }, [busca, data, direcao, getAliasForAd, ordem, remoto]);

  useEffect(() => { setPagina(1); }, [busca, ordem, direcao, tamanho]);
  const linhas: LinhaAnuncio[] = remoto
    ? (consulta.data?.itens ?? []).map(item => ({ chave: item.chave, nome: item.nome, apelido: item.apelido, leads: item.leads, conversoes: item.conversoes }))
    : legados.slice((pagina - 1) * tamanho, pagina * tamanho);
  const totalItens = remoto ? (consulta.data?.totalItens ?? 0) : legados.length;
  const totalLeads = remoto ? (consulta.data?.totalLeads ?? 0) : data.reduce((soma, item) => soma + item.leads, 0);
  const totalConversoes = remoto ? (consulta.data?.totalConversoes ?? 0) : data.reduce((soma, item) => soma + item.conversoes, 0);
  const totalPaginas = Math.max(1, Math.ceil(totalItens / tamanho));
  // isLoading bloqueia o primeiro carregamento; isFetching mantém a tabela visível
  // durante uma atualização em segundo plano, evitando o efeito de piscar ao ordenar.
  const carregando = remoto && consulta.isLoading;
  const comErro = remoto && Boolean(consulta.error);
  const bloqueado = carregando || comErro || isSaving;

  useEffect(() => {
    // Só corrige uma página inexistente após a resposta remota; durante o debounce não voltamos ao início.
    if (!remoto || consulta.data) setPagina(atual => Math.min(atual, totalPaginas));
  }, [consulta.data, remoto, totalPaginas]);

  const iniciarEdicao = (nome: string) => {
    if (bloqueado) return;
    setEditando(nome);
    setApelido(getAliasForAd(nome) ?? nome);
    setErroApelido(null);
    setApelidoSalvo(false);
  };

  const cancelar = () => {
    setEditando(null);
    setApelido('');
    setErroApelido(null);
  };
  const salvar = async (nome: string) => {
    if (!apelido.trim()) { setErroApelido('Digite um apelido antes de salvar.'); return; }
    try {
      // A escrita continua restrita a uma ação explícita de salvar da pessoa usuária.
      await saveAlias({ adNameOriginal: nome, alias: apelido.trim() });
      setApelidoSalvo(true);
      cancelar();
    } catch {
      setApelidoSalvo(false);
      setErroApelido('Não foi possível salvar o apelido. Tente novamente.');
    }
  };

  const linha = (item: LinhaAnuncio, indice: number, mobile = false) => {
    // O apelido agregado já chega com o resultado remoto; o índice local cobre o fallback legado.
    const nome = item.apelido ?? getAliasForAd(item.nome) ?? item.nome;
    const taxaAtual = taxa(item.leads, item.conversoes);
    const emEdicao = editando === item.nome;
    const abrir = () => {
      if (!bloqueado && !emEdicao) onAdClick?.(item.nome);
    };
    const editor = (
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={apelido}
          onChange={event => setApelido(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') void salvar(item.nome);
            if (event.key === 'Escape') cancelar();
          }}
          aria-label={`Apelido de ${item.nome}`}
        />
        <Button
          type="button"
          size="icon"
          className="h-10 w-10 shrink-0"
          disabled={isSaving}
          onClick={() => void salvar(item.nome)}
          aria-label="Salvar apelido"
        >
          <Check className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-10 w-10 shrink-0"
          onClick={cancelar}
          aria-label="Cancelar edição"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );

    if (mobile) {
      return (
        <li key={item.chave} className="border-b border-border py-3 last:border-b-0">
          {emEdicao ? editor : (
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-2">
              <button
                type="button"
                disabled={bloqueado || !onAdClick}
                onClick={abrir}
                className="min-h-11 truncate text-left text-sm font-medium text-foreground disabled:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {nome}
              </button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-10 w-10"
                disabled={bloqueado}
                onClick={() => iniciarEdicao(item.nome)}
                aria-label={`Editar apelido de ${nome}`}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <div className="col-span-2 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                <span><strong className="block tabular-nums text-foreground">{item.leads}</strong>Leads</span>
                <span><strong className="block tabular-nums text-foreground">{item.conversoes}</strong>Conversões</span>
                <span><strong className="block tabular-nums text-foreground">{taxaAtual.toFixed(1)}%</strong>Taxa</span>
              </div>
            </div>
          )}
        </li>
      );
    }

    return (
      <tr key={item.chave} className="h-12 border-b border-border last:border-b-0 hover:bg-muted/30">
        <td className="w-10 pl-3 text-xs tabular-nums text-muted-foreground">
          {(pagina - 1) * tamanho + indice + 1}
        </td>
        <td className="max-w-0 pr-2">
          {emEdicao ? editor : (
            <button
              type="button"
              disabled={bloqueado || !onAdClick}
              onClick={abrir}
              title={`Nome original: ${item.nome}`}
              className="min-h-10 max-w-full truncate text-left text-sm font-medium text-foreground disabled:opacity-100 hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {nome}
            </button>
          )}
        </td>
        <td className="w-12">
          {!emEdicao && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-9 w-9"
              disabled={bloqueado}
              onClick={() => iniciarEdicao(item.nome)}
              aria-label={`Editar apelido de ${nome}`}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}
        </td>
        <td className="w-20 pr-3 text-right text-sm tabular-nums">{item.leads}</td>
        <td className="w-28 pr-3 text-right text-sm tabular-nums">{item.conversoes}</td>
        <td className="w-20 pr-4 text-right text-sm tabular-nums">{taxaAtual.toFixed(1)}%</td>
      </tr>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent
        className="flex h-[94dvh] min-h-0 w-[calc(100vw-1rem)] max-w-[1280px] flex-col gap-3 overflow-hidden p-4 sm:h-[92dvh] sm:p-5"
      >
        <DialogHeader className="shrink-0 space-y-1 pr-8">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-accent-foreground" aria-hidden="true" />
            Performance detalhada de anúncios
          </DialogTitle>
          <DialogDescription>
            Pesquise, ordene e abra os anúncios atribuídos ao período.
          </DialogDescription>
        </DialogHeader>

        <div className="grid shrink-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_150px_150px_112px]">
          <div className="relative">
            <Label htmlFor="performance-anuncios-search" className="sr-only">Buscar anúncios</Label>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              id="performance-anuncios-search"
              className="h-10 pl-9"
              value={busca}
              onChange={event => setBusca(event.target.value)}
              placeholder="Buscar anúncio"
            />
          </div>
          <Select value={ordem} onValueChange={(value: Ordem) => setOrdem(value)}>
            <SelectTrigger className="h-10" aria-label="Ordenar anúncios por"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="leads">Leads</SelectItem>
              <SelectItem value="conversoes">Conversões</SelectItem>
              <SelectItem value="taxa">Taxa</SelectItem>
              <SelectItem value="nome">Nome</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            className="h-10 justify-between"
            onClick={() => setDirecao(direcao === 'asc' ? 'desc' : 'asc')}
            aria-label={`Ordenação ${direcao === 'asc' ? 'crescente' : 'decrescente'}. Alternar`}
          >
            <span>{direcao === 'asc' ? 'Crescente' : 'Decrescente'}</span>
            <ArrowUpDown className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Select value={String(tamanho)} onValueChange={value => setTamanho(Number(value) as Tamanho)}>
            <SelectTrigger className="h-10" aria-label="Anúncios por página"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="20">20 por página</SelectItem>
              <SelectItem value="50">50 por página</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid shrink-0 grid-cols-3 gap-3 border-y border-border py-2 text-sm">
          <span><strong className="tabular-nums">{totalItens}</strong><span className="ml-1 text-muted-foreground">anúncios</span></span>
          <span><strong className="tabular-nums">{totalLeads}</strong><span className="ml-1 text-muted-foreground">leads</span></span>
          <span><strong className="tabular-nums">{totalConversoes}</strong><span className="ml-1 text-muted-foreground">conversões</span></span>
        </div>

        {erroApelido && <p role="alert" className="shrink-0 text-sm text-destructive">{erroApelido}</p>}
        {apelidoSalvo && <p role="status" className="shrink-0 text-sm text-accent-foreground">Apelido salvo.</p>}

        <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite" aria-busy={consulta.isFetching}>
          {comErro ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <p role="alert" className="text-sm text-muted-foreground">Não foi possível carregar os anúncios.</p>
              <Button type="button" variant="outline" onClick={() => void consulta.refetch()}>Tentar novamente</Button>
            </div>
          ) : carregando && !linhas.length ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Carregando anúncios…</div>
          ) : !linhas.length ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Nenhum anúncio encontrado.</div>
          ) : (
            <>
              <table className="hidden w-full table-fixed border-collapse sm:table">
                <thead className="sticky top-0 z-10 bg-background text-left text-xs font-medium text-muted-foreground">
                  <tr>
                    <th className="w-10 py-2 pl-3">#</th>
                    <th className="py-2">Anúncio</th>
                    <th className="w-12 py-2" aria-label="Editar apelido" />
                    <th className="w-20 py-2 pr-3 text-right">Leads</th>
                    <th className="w-28 py-2 pr-3 text-right">Conversões</th>
                    <th className="w-20 py-2 pr-4 text-right">Taxa</th>
                  </tr>
                </thead>
                <tbody>{linhas.map((item, indice) => linha(item, indice))}</tbody>
              </table>
              <ul className="sm:hidden">{linhas.map((item, indice) => linha(item, indice, true))}</ul>
            </>
          )}
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <p className="text-xs tabular-nums text-muted-foreground">
            {totalItens ? `Exibindo ${(pagina - 1) * tamanho + 1}-${Math.min(pagina * tamanho, totalItens)} de ${totalItens}` : 'Sem anúncios para exibir'}
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="h-10" disabled={pagina === 1 || bloqueado} onClick={() => setPagina(valor => Math.max(1, valor - 1))}>Anterior</Button>
            <span className="text-xs tabular-nums text-muted-foreground">{pagina}/{totalPaginas}</span>
            <Button type="button" variant="outline" size="sm" className="h-10" disabled={pagina >= totalPaginas || bloqueado} onClick={() => setPagina(valor => Math.min(totalPaginas, valor + 1))}>Próxima</Button>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
